import { faFileInvoice, faFileLines } from '@fortawesome/pro-light-svg-icons'
import { useCallback } from 'react'
import { useNavigate } from 'react-router'
import { ButtonSecondary } from '../../../../../../baseComponents/Button'
import { ModalBigCentered } from '../../../../../../baseComponents/Modal'
import TwinTrans from '../../../../../../baseComponents/TwinTrans'
import twinFetchPost from '../../../../../../utils/globals/data'
import locations from '../../../../../../utils/globals/locations'
import useTwinTranslation from '../../../../../../utils/hooks/useTwinTranslation'
import { changeErrorMessage } from '../../../../../../utils/reducers/reduxDispatch'
import { getPreparePricesConfigParam, getSelectedWorkingCenter, getUserPermissions } from '../../../../../../utils/reducers/getters'
import { parseSelectedTableForProcessing } from '../../../../../../app/erp/screens/Logistic/QuoteParent/SellParent/functions'
import { OrderData } from '../../../OrderLayout/types'
import { checkSellDeliveryNoteFromQuoteOrderAllOk, checkSellInvoiceFromQuoteOrderAllOk, checkSellOrderFromQuoteOrderAllOk } from '../../../../../../utils/globals/order/order'
import { ProcessButton } from '../../../OrderLayout/OrderButtons'
import { permissionCheck } from '../../../../../../baseComponents/PermissionChecker/function'
import { SetEditDeleteModalTy } from '../../../../../../utils/hooks/useEditDeleteModal'
import './modalProcessQuote.sass'
import useIsLoading from '../../../../../../utils/hooks/useIsLoading'
import LoadingSpinner from '../../../../../../baseComponents/LoaderDecider/LoadingSpinner'
import { parseOrderTableData } from '../../function'
import { HeaderDataCalculate } from '@teinor/erp/types/company/order/general'
import { CalculateParamsTy } from '../../../../../../baseComponents/Displays/DisplayPrice/types'
import { TwinDictionary } from '../../../../../../utils/globals/dictionary'
import { OrderTableData } from '@teinor/erp/prices/quote/totals'

interface ModalProcessQuoteProps { 
    data: OrderData
    setOpenModal: SetEditDeleteModalTy
    OrderId: number
}

const ModalProcessQuote: React.FC<ModalProcessQuoteProps> = ({ setOpenModal, data, OrderId }) => {
    return (
        <ModalBigCentered opened={true} setOpened={() => setOpenModal(null)} onClickOut={false} className='flex flex-col modal_process_quote_order'>
            <ProcessQuoteOrder {...{ data, setOpenModal, OrderId }} />
            <ButtonSecondary onClick={() => setOpenModal(null)} className='w-100 ml-auto mt-30 regular14'>
                <TwinTrans transKey='back'>Volver</TwinTrans>
            </ButtonSecondary>
        </ModalBigCentered>
    )
}

interface ProcessQuoteOrderProps extends ProcessQuoteOrderLogicProps {}

const ProcessQuoteOrder: React.FC<ProcessQuoteOrderProps> = ({ data, setOpenModal, OrderId }) => {
    const { t } = useTwinTranslation()
    const { finalizeOrder, messageOrder, messageDeliveryNote, messageInvoice, loading } = useProcessQuoteOrderLogic({ data, setOpenModal, OrderId })
    return (
        <div className='process_quote_order'>
            <h4 className='text-start'><TwinTrans transKey='processQuoteOrderFromSelectedProducts'>Procesar presupuesto a partir de los productos seleccionados</TwinTrans></h4>
            <div className='flex flex-col gap-y-15 mt-30'>
                {loading ? <LoadingSpinner /> :
                    <div className='flex gap-30 flex-auto'>
                        <ProcessButton finalizeOrder={finalizeOrder} url='/api/app/order/quote/createSellOrderByQuoteOrder' navigateTo={locations.sellOrderSingle} text={t('createSellOrder', 'Crear pedido')} icon={faFileInvoice} messageError={messageOrder} />
                        <ProcessButton finalizeOrder={finalizeOrder} url='/api/app/order/quote/createSellDeliveryNoteByQuoteOrder' navigateTo={locations.sellDeliveryNoteSingle} text={t('createDeliveryNote', 'Crear albarán')} icon={faFileLines} messageError={messageDeliveryNote} />
                        <ProcessButton finalizeOrder={finalizeOrder} url='/api/app/order/quote/createSellInvoiceByQuoteOrder' navigateTo={locations.sellInvoiceSingle} text={t('createInvoice', 'Crear factura')} icon={faFileInvoice} messageError={messageInvoice} />
                    </div>
                }
            </div>
        </div>
    )
}

interface ProcessQuoteOrderLogicProps {
    data: OrderData
    setOpenModal: SetEditDeleteModalTy
    OrderId: number
}

const useProcessQuoteOrderLogic = ({ data, setOpenModal, OrderId }: ProcessQuoteOrderLogicProps) => {
    const navigate = useNavigate()
    const { t } = useTwinTranslation()
    const errorMsg = t('noProductStockOnLine', 'No hay stock del producto en la línea')
    const workingCenter = getSelectedWorkingCenter()
    const messageOrder = checkSellOrderFromQuoteOrderAllOk(data, t)
    const messageDeliveryNote = checkSellDeliveryNoteFromQuoteOrderAllOk(data, t)
    const messageInvoice = checkSellInvoiceFromQuoteOrderAllOk(data, t)
    const noWarehouse = t('noWarehouse', 'Este centro de trabajo no tiene almacén')
    const { startLoading, endLoading, loading } = useIsLoading()

    const finalizeOrder = useCallback(async (url: string, navigateUrl: string, extraProps?: TwinDictionary) => {
        startLoading?.()
        const mySelectedProducts = parseSelectedTableForProcessing(data || [])
        const response = await twinFetchPost(url, { selectedProducts: mySelectedProducts.forSend.products, OrderId, WorkingCenterId: workingCenter.id, ...extraProps })
        const responseJSON = await response.json()
        if (response.status === 200) {
            navigate(navigateUrl + '/' + responseJSON.id)
            setOpenModal(null)
        } else {
            if (responseJSON.errors[0].msg) {
                if (responseJSON.errors[0].msg === 'NoWarehouse') {
                    changeErrorMessage(noWarehouse)
                } else if (responseJSON.errors[0].msg === 'Not payment method') {
                    const calculateParams = getPreparePricesConfigParam() as CalculateParamsTy
                    const headerDataCalculate: HeaderDataCalculate = {
                        OrderCosts: data?.header?.OrderCosts,
                        ParamsRoundingDecimals: calculateParams,
                        OrderShipping: data?.header?.OrderShipping,
                        isFinished: data?.header?.isFinished,
                        OrderAccounts: data?.header?.OrderAccounts
                    }
                    let selectedTableForCalculate = data?.tableData
                    if (data?.selectedTableData) {
                        selectedTableForCalculate = []
                        for (const element of data?.selectedTableData) {
                            selectedTableForCalculate.push(data?.tableData[data?.dictPosTableData[element.id]])
                        }
                    }
                    const totalsOrderTableData = parseOrderTableData(selectedTableForCalculate as OrderTableData, headerDataCalculate)
                    setOpenModal({ type: 'delete', allRowData: { extraProps: { OrderId: data?.header?.id, WorkingCenterId: data?.header?.WorkingCenterId, selectedProducts: mySelectedProducts.forSend.products }, createUrl: url, navigateUrl, total: totalsOrderTableData.totals.total, finalizeOrder } })
                } else if (responseJSON.errors[0].msg.includes('noStock')) {
                    const skipStock = permissionCheck(getUserPermissions('sellOther.sellWithoutStock'), 'read')
                    if (skipStock) {
                        setOpenModal({ type: 'edit', allRowData: { extraProps: { OrderId: data?.header?.id, selectedProducts: mySelectedProducts.forSend.products, WorkingCenterId: data?.header?.WorkingCenterId, skipCheckOperateStock: true }, finalizeOrder, createUrl: url, navigateUrl, data } })
                    } else {
                        const msgArr = responseJSON.errors[0].msg.split('-')
                        let myMsg = errorMsg + ' ' + msgArr[1]
                        if (msgArr[2]) {
                            myMsg += ' nº: ' + msgArr[2]
                        }
                        changeErrorMessage(myMsg)
                        setOpenModal(null)
                    }
                } else {
                    changeErrorMessage(responseJSON.errors[0].msg)
                    setOpenModal(null)
                }
            }
        }
        endLoading?.()
    }, [data, navigate, setOpenModal, OrderId, workingCenter, errorMsg, startLoading, endLoading, noWarehouse])

    return { finalizeOrder, messageOrder, messageDeliveryNote, messageInvoice, loading }
}

export default ModalProcessQuote